import React, { useState, useEffect } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { regions } from '../../data/regions';
import { Button, Container, Row, Col } from 'react-bootstrap';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit'
import { FullScreenPricesModal } from './FullScreenPricesModal';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { selectFilter } from 'react-bootstrap-table2-filter';
import { generateCsv } from '../../components/AmazonInventory/generateCsv'
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import 'react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css';
import FileSaver from 'file-saver';

const { SearchBar } = Search;

export const TableToRender = (props) => {
  const [show, setShow] = useState(false);
  const [cachedSelectedRows, setCachedSelectedRows] = useState([]);

  //stores original version in state for on close
  useEffect(() => {
    if (show) {
      const obj = JSON.stringify(props.selectedRows); //not sure why have stringify this but otherwise it keeps updating
      setCachedSelectedRows(JSON.parse(obj));
    }
  }, [show]);

  //makes and sends csv
  function makeAndSendCsv(products) {
    const csvText = generateCsv(products, ',');
    const blob = new Blob([csvText], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, 'products.csv');
  }

  //manages selected row state
  function handleOnSelect(row, isSelect) {
    if (isSelect) {
      props.setSelectedRows([...props.selectedRows, row]);
    } else if (!isSelect) {
      props.setSelectedRows(props.selectedRows.filter((product) => product.id !== row.id));
    }
    console.log('1', props);
  }

  function handleSelectAll(isSelect, rows) {
    if (!isSelect) {
      props.setSelectedRows([])
    } else if (isSelect) {
      props.setSelectedRows([...props.selectedRows, ...rows])
    }
    console.log(isSelect, rows)
  }

  //set data for modal when click edit
  function onClickEdit(row) {
    if (props.selectedRows.length === 0) {
      //when close modal, need to clear selectedrows if length === 1
      props.setSelectedRows([row]);
    }
    //prevent modal opening if certain product statuses selected
    const unapproved = Object.values(props.selectedRows).filter((product) => {
      return product.approved === false;
    });
    if (unapproved.length > 1) {
      return window.alert('Products must be individually approved before they can be bulk edited');
    } else if (unapproved.length > 0 && unapproved.length < props.selectedRows.length) {
      return window.alert('Cannot open mix of approved and unapproved products');
    } else if (unapproved.length > 8 || props.selectedRows.length > 8) {
      return window.alert('Cannot select more than 8 products');
    }
    //display modal
    setShow(true);
  }

  //begin react table
  const products = [...props.products]; //amazonInventory

  const selectRow = {
    mode: 'checkbox',
    style: { background: 'blue' },
    onSelect: (row, isSelect) => handleOnSelect(row, isSelect),
    onSelectAll: (isSelect, rows) => handleSelectAll(isSelect, rows)
  };

  //filter select options
  const asinSelectOptions = {
    Listed: 'ASIN',
    'Not listed': 'No ASIN',
  };

  const approvedSelectOptions = {
    true: 'Approved',
    false: 'Not approved',
  };


  //columns
  const columns = [
    {
      dataField: 'id',
      text: 'Product ID',
    },
    {
      dataField: 'name',
      text: 'Product Name',
      filterValue: (cell) => cell.includes('Size') || cell.includes('size') ? true : false,
      filter: selectFilter({
        options: {
          true: 'Sized Products',
          false: 'Non-Sized Products'
        }
      })
    },
    {
      isDummyField: true,
      dataField: 'name',
      hidden: true

    },
    {
      dataField: 'asin',
      text: 'ASIN',
      filter: selectFilter({
        options: asinSelectOptions,
      }),
      filterValue: (cell) => (cell ? 'Listed' : 'Not listed'),
    },
    {
      dataField: 'costPrice',
      text: 'Cost Price',
      formatter: (cell) => '£' + parseFloat(cell).toFixed(2),
      filterValue: (cell) => cell === 0 ? true : false, //this value will be used in the filter below
      filter: selectFilter({
        options: {
          true: 'Zero Cost',
          false: 'Not Zero Cost'
        },
      })
    },
    {
      dataField: 'exclude',
      text: 'Exclude',
      filterValue: (cell) => Boolean(cell),
      filter: selectFilter({
        options: {
          true: 'True',
          false: 'False'
        }
      }),
      formatter: (cell) => cell ? 'True' : 'False'
    },
    {
      dataField: 'weight',
      text: 'Weight',
      formatter: (cell) => cell + 'kg',
    },
    {
      dataField: 'packaging',
      text: 'Packaging',
      formatter: (cell) =>
        cell === 'box' ? <FontAwesomeIcon icon={faBox} color='brown' /> : <FontAwesomeIcon icon={faEnvelope} />,
    },
    {
      dataField: 'lockBaseAttributes',
      text: 'Base lock',
      formatter: (cell) => (cell ? 'Locked' : 'Unlocked'),
    },
    {
      dataField: 'regionPricesLocked',
      text: 'Region Prices Locked',
      isDummyField: true,
      formatter: (cell, row) =>
        regions.map((region) => {
          if (row[region].lockRegionPrices) {
            return region.toUpperCase() + ' ';
          }
        }),
    },
    {
      dataField: 'blendedMinMargin',
      text: 'Blended Min Margin',
      formatter: (cell, row) =>
        (
          regions.reduce((acc, region) => {
            return acc + parseFloat(row[region].minTargetMargin);
          }, 0) / regions.length
        ).toFixed(1) + '%',
    },
    {
      text: 'Blended Max Margin',
      isDummyField: true,
      formatter: (cell, row) =>
        (
          regions.reduce((acc, region) => {
            return acc + parseFloat(row[region].maxTargetMargin);
          }, 0) / regions.length
        ).toFixed(1) + '%',
    },
    {
      text: 'Edit',
      isDummyField: true,
      formatter: (cell, row) => (
        <>
          <Button
            size='sm'
            onClick={() => onClickEdit(row)}
            disabled={
              props.selectedRows.length >= 1 && !props.selectedRows.map((product) => product.id).includes(row.id)
            }
          >
            Edit
          </Button>
        </>
      ),
      formatExtraData: props.selectedRows,
    },
    {
      dataField: 'approved',
      text: 'Approved',
      formatter: (cell) => (cell ? 'Approved' : 'Not Approved'),
      filter: selectFilter({
        options: approvedSelectOptions,
      }),
    },
    {
      dataField: 'createdAt',
      text: 'Date Added',
      formatter: (cell) => cell.slice(0, 10),
    },
  ];


  return (
    <>
      <ToolkitProvider keyField='id' data={products} columns={columns} search>
        {(props) => (
          <div>
            <Container>
              <Row>
                <Col xs='1' className='mt-2'>
                  <h3>Search:</h3>
                </Col>
                <Col xs='9' className='mt-2'>
                  <SearchBar {...props.searchProps} style={{ width: '50vw' }} />
                </Col>
                <Col>
                  <Button
                    onClick={() => {
                      makeAndSendCsv(products);
                    }}
                  >
                    Generate CSV for PPP
                  </Button>
                </Col>
              </Row>
            </Container>
            <hr />
            <BootstrapTable
              {...props.baseProps}
              keyField='id'
              data={products}
              columns={columns}
              selectRow={selectRow}
              pagination={paginationFactory({ sizePerPage: 50 })}
              filter={filterFactory()}
            />
          </div>
        )}
      </ToolkitProvider>

      <FullScreenPricesModal
        cachedSelectedRows={cachedSelectedRows}
        setCachedSelectedRows={setCachedSelectedRows}
        selectedRows={props.selectedRows}
        setSelectedRows={props.setSelectedRows}
        selectedRegions={props.selectedRegions}
        setSelectedRegions={props.setSelectedRegions}
        updateBaseProductAttribute={(e, index) => {
          props.updateBaseProductAttribute(e, index);
        }}
        recalculateFromBasePrices={props.recalculateFromBasePrices}
        updateRegionalProductAttribute={(e, region, index, value) => {
          props.updateRegionalProductAttribute(e, region, index, value);
        }}
        updateRegionArr={(e) => {
          props.updateRegionArr(e);
        }}
        show={show}
        setShow={setShow}
        updateStateOnHideModal={(e) => {
          props.updateStateOnHideModal(e);
        }}
      />
    </>
  );
};
